import logger from '@leaflink/snitch';
import { LocationQuery } from 'vue-router';

export type SegmentTrackPageDataResult = Record<string, unknown>;
export type SegmentTrackPageDataPayload = Record<string, SegmentTrackPageDataResult & { timestamp?: number }>;

export interface EncodeSegmentTrackPageDataArgs {
  /**
   * Object containing the segment track page data separated by namespace
   */
  data: SegmentTrackPageDataPayload;
}

/**
 * Encodes the segment track page data to query parameters
 * @param Object - Object containing the segment track page data separated by namespace
 * @returns String - Encoded query parameters
 *
 * @example
 * ```javascript
 * const data = {
 *  algolia: {
 *    query_id: '321',
 *  },
 *  fullStory: {
 *    productId: '123',
 *  },
 * }
 *
 * const query = encodeSegmentTrackPageDataToQueryParams(data);
 * ```
 *
 * **Obs.:** A timestamp will be added to the metadata when encoding the query parameters. To override this, provide a timestamp value of `false` in the metadata.
 *
 * ```javascript
 * const data = {
 *  algolia: {
 *    query_id: '321',
 *    timestamp: 7681234544,
 *  },
 * }
 * ```
 */
export function encodeSegmentTrackPageDataToQueryParams({ data }: EncodeSegmentTrackPageDataArgs) {
  if (!Object.keys(data).length) return '';

  const transformedEntries = Object.entries(data)
    .filter(([_, value]) => value !== null && value !== undefined)
    .map(([key, value]) => {
      const filteredValue = Object.fromEntries(
        Object.entries(value).filter(([_, value]) => value !== null && value !== undefined),
      );
      const encodedValue = { ...filteredValue, timestamp: value.timestamp || Date.now() };
      return [key, encodedValue];
    });

  const result = Object.fromEntries(transformedEntries);

  return encodeURIComponent(JSON.stringify(result));
}

export interface EncodeSegmentTrackPageDataToQueryArgs {
  /**
   * Query object from the router
   */
  query: LocationQuery;
}

const SEGMENT_TRACK_DATA_STALE_TIME = 5 * 60 * 1000; // 5 minutes

/**
 * Get segment page event data from the query string named `segment_page_event_data`.
 * The value must be an encoded JSON object returned by encodeSegmentTrackPageDataToQueryParams().
 *
 * @param Object - Object containing the query object from the router
 *
 * @returns Page metadata object
 *
 * @example
 * ```
 * const encodedQuery = {
 *  segment_page_event_data: encodedURIComponent(JSON.stringify({
 *      algolia: {
 *        query_id: '123',
 *        timestamp: 1234567890,
 *      },
 *  }))
 * }
 *
 * decodeSegmentTrackPageDataFromQuery({ query: encodedQuery });
 *
 * // returns
 * {
 *  query_id: '123',
 * }
 * ```
 *
 * **Obs.:** timestamp is a special case and will be used to check if the metadata is stale. It will not be included in the returned object.
 */
export function decodeSegmentTrackPageDataFromQueryParams({ query }: EncodeSegmentTrackPageDataToQueryArgs) {
  let trackData: SegmentTrackPageDataPayload = {};

  for (const [key, value] of Object.entries(query)) {
    if (!value || Array.isArray(value)) {
      logger.error('Invalid page metadata value', { key, value });
      continue;
    }

    if (key !== 'segment_page_event_data') continue;

    try {
      const parsedTrackData = JSON.parse(decodeURIComponent(value));
      trackData = { ...trackData, ...parsedTrackData };
    } catch (error) {
      logger.error('Failed to parse segment track page data', { error, value });
    }
  }

  return Object.entries(trackData).reduce((acc, [_, value]) => {
    const { timestamp, ...rest } = value;

    // Check if the timestamp is stale. If so, don't include the related value in the track data
    if (timestamp && Date.now() - Number(timestamp) > SEGMENT_TRACK_DATA_STALE_TIME) {
      return acc;
    }

    return { ...acc, ...rest };
  }, {} as SegmentTrackPageDataResult);
}
